import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../shared/keys";
import type { AgentConfig, AgentUpdate } from "../shared/types";

const PREVIEW_DEBOUNCE_MS = 500;
const MEMBERS_LIMIT = 200;

export function useSettingsMutations(agentId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const path = { params: { path: { agent_id: agentId } } };
  const store = (config: AgentConfig) => {
    client.setQueryData(buildKeys.agent(ws, agentId), config);
    void client.invalidateQueries({ queryKey: buildKeys.agents(ws) });
  };
  const update = useMutation({ mutationFn: (body: AgentUpdate) => unwrap(api.PATCH("/v1/agents/{agent_id}", { ...path, body })), onSuccess: store });
  const restore = useMutation({
    mutationFn: (version: number) => unwrap(api.POST("/v1/agents/{agent_id}/versions/{version}/restore", { params: { path: { agent_id: agentId, version } } })),
    onSuccess: (config) => {
      store(config);
      void client.invalidateQueries({ queryKey: buildKeys.versions(ws, agentId) });
    },
  });
  const publish = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/agents/{agent_id}/publish", path)),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: buildKeys.versions(ws, agentId) }),
      client.invalidateQueries({ queryKey: buildKeys.agent(ws, agentId) }),
      client.invalidateQueries({ queryKey: buildKeys.agents(ws) }),
    ]),
  });
  const duplicate = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/agents/{agent_id}/duplicate", path)),
    onSuccess: () => client.invalidateQueries({ queryKey: buildKeys.agents(ws) }),
  });
  const remove = useMutation({
    mutationFn: (confirm: string) => unwrap(api.DELETE("/v1/agents/{agent_id}", { ...path, body: { confirm } })),
    onSuccess: () => client.invalidateQueries({ queryKey: buildKeys.agents(ws) }),
  });
  return { update, restore, publish, duplicate, remove };
}

export function useVersions(agentId: string, enabled: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.versions(ws, agentId),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/versions", { params: { path: { agent_id: agentId } } })),
    enabled,
  });
}

/** Member names for version authors (needs team.view; falls back to "A team member"). */
export function useMemberNames(enabled: boolean): (userId: string | null | undefined) => string | null {
  const api = useApi();
  const ws = useWorkspaceId();
  const query = useQuery({
    queryKey: buildKeys.members(ws),
    queryFn: () => unwrap(api.GET("/v1/members", { params: { query: { limit: MEMBERS_LIMIT } } })),
    enabled,
    retry: false,
  });
  return (userId) => query.data?.items.find((m) => m.user_id === userId)?.name ?? null;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => { setDebounced(value); }, ms);
    return () => { clearTimeout(timer); };
  }, [value, ms]);
  return debounced;
}

/** `POST prompt-preview`: resolved variable values + unknown variables for the current draft. */
export function usePromptPreview(agentId: string, instructions: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const text = useDebounced(instructions, PREVIEW_DEBOUNCE_MS);
  return useQuery({
    queryKey: buildKeys.promptPreview(ws, agentId, text),
    queryFn: () => unwrap(api.POST("/v1/agents/{agent_id}/prompt-preview", { params: { path: { agent_id: agentId } }, body: { instructions: text } })),
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/** Latest eval set result for the Retrieval footer (optional: hidden on 402/403; `enabled` skips the call when the plan has no Evaluations). */
export function useLatestEval(agentId: string, enabled = true) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.evalSets(ws, agentId),
    queryFn: () => unwrap(api.GET("/v1/eval-sets", { params: { query: { agent_id: agentId } } })),
    retry: false,
    enabled,
  });
}
